import React, { useState } from 'react';
import { outfitAdminApi } from '../../../../services/outfitAdminApi';

function Step4ReviewPublish({
  formData,
  selectedCCI,
  selectedFacets,
  lookCount,
  onPublishSuccess,
  onBack,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handlePublish = async () => {
    try {
      setSubmitting(true);
      setError('');

      const payload = {
        name: formData.name.trim(),
        gender: formData.gender,
        image: formData.image?.trim() || '',
        category_combination_id: selectedCCI.category_combination_id,
        selected_facets: selectedFacets,
      };

      const response = await outfitAdminApi.createTrendOutfit(payload);
      if (response && response.status === 'success') {
        onPublishSuccess(response.data);
      } else {
        throw new Error(response?.message || 'Failed to publish trend');
      }
    } catch (err) {
      console.error('Failed to create trend:', err);
      setError(err.message || 'Failed to publish trend collection');
    } finally {
      setSubmitting(false);
    }
  };

  const hasFacetConstraints = Object.keys(selectedFacets).length > 0;

  return (
    <div className="outfit-card">
      <div className="outfit-card-header">
        <div>
          <h2 className="outfit-card-title">Step 4: Review & Publish Trend Collection</h2>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            Verify your trend configuration and curated looks before publishing.
          </span>
        </div>
      </div>

      {error && (
        <div className="outfit-alert error">
          <span>{error}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        {/* Trend Identity & Cover */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', color: '#475569', marginBottom: '12px' }}>
            Trend Identity
          </h3>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
            {formData.image ? (
              <img
                src={formData.image}
                alt={formData.name}
                style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '8px',
                  background: '#e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  color: '#94a3b8',
                }}
              >
                No Cover
              </div>
            )}

            <div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                {formData.name}
              </div>
              <span className={`cci-gender-badge ${formData.gender}`} style={{ marginTop: '6px' }}>
                {formData.gender}
              </span>
            </div>
          </div>
        </div>

        {/* Selected Formula & Curated Looks */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', color: '#475569', marginBottom: '12px' }}>
            Formula & Yield
          </h3>

          <div style={{ marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>CCI Formula:</span>
            <div className="cci-formula-grid" style={{ marginTop: '4px' }}>
              {selectedCCI?.combination &&
                Object.entries(selectedCCI.combination).map(([slot, cat]) => (
                  <span key={slot} className="cci-slot-pill">
                    <span className="cci-slot-label">{slot.replace(/_/g, ' ')}:</span>
                    <span className="cci-slot-value">{Array.isArray(cat) ? cat.join(', ') : cat}</span>
                  </span>
                ))}
            </div>
          </div>

          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
              Curated Looks Yield:
            </span>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#16a34a', marginTop: '2px' }}>
              {lookCount !== undefined ? lookCount : selectedCCI?.total_looks_count || 0} Looks
            </div>
          </div>
        </div>
      </div>

      {/* Applied Visual Facet Constraints */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', color: '#475569', marginBottom: '12px' }}>
          Applied Visual Facet Constraints
        </h3>

        {!hasFacetConstraints ? (
          <span style={{ fontSize: '13px', color: '#94a3b8' }}>
            No visual property constraints applied (all look variations under this CCI will be included).
          </span>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
            {Object.entries(selectedFacets).map(([slot, propObj]) => (
              <div key={slot} style={{ background: '#ffffff', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', color: '#0f172a', marginBottom: '6px' }}>
                  {slot.replace(/_/g, ' ')}
                </div>
                {Object.entries(propObj).map(([propName, vals]) => (
                  <div key={propName} style={{ fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: '#64748b', fontWeight: '600' }}>{propName}: </span>
                    <span style={{ color: '#000000', fontWeight: '700' }}>{vals.join(', ')}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="wizard-nav-bar">
        <button
          type="button"
          className="outfit-btn outfit-btn-secondary"
          disabled={submitting}
          onClick={onBack}
        >
          &larr; Back to Facets
        </button>

        <button
          type="button"
          className="outfit-btn outfit-btn-primary"
          style={{ minWidth: '180px' }}
          disabled={submitting}
          onClick={handlePublish}
        >
          {submitting ? 'Publishing Trend...' : '✓ Publish Trend Collection'}
        </button>
      </div>
    </div>
  );
}

export default Step4ReviewPublish;
